import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper } from '@mui/material';
import useThemeStyles from '../../../../../hooks/useThemeStyles';

const TitlePage = ({ title = '', wordCount = 0, pseudonym = '' }) => {
  const { colors, components, isDark } = useThemeStyles();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,

        position: 'relative', // Ensure positioning context
      }}
    >
      {/* Paper-like container with proper aspect ratio */}
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: '10in', // Wider title page width
          aspectRatio: '8.5/11', // Standard paper aspect ratio (8.5:11)
          minHeight: '11in', // Minimum height
          maxHeight: '90vh', // Don't exceed viewport height
          position: 'relative',
          backgroundColor: isDark ? '#1e293b' : colors.paper.background,
          backgroundImage: isDark ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' : 'none',
          border: isDark ? `1px solid ${colors.border.subtle}` : components.paper.border,
          boxShadow: isDark
            ? '0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)'
            : components.paper.shadow,
          display: 'flex',
          flexDirection: 'column',
          p: 4,
          boxSizing: 'border-box',
          '&:hover': {
            boxShadow: isDark
              ? '0 6px 8px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.3)'
              : components.paper.shadowHover,
          },
        }}
      >
        {/* Word Count - positioned within the paper */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'serif',
            color: isDark ? '#cbd5e1' : colors.text.secondary,
            fontSize: '0.875rem',
            position: 'absolute',
            top: 16,
            right: 16, // Move to top-right corner for better visibility
          }}
        >
          {wordCount ? `${wordCount.toLocaleString()} words` : '0 words'}
        </Typography>

        {/* Main content - centered vertically and horizontally */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            px: 2,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontFamily: 'serif',
              fontWeight: 'bold',
              color: isDark ? '#e2e8f0' : colors.text.primary,
              lineHeight: 1.2,
              textShadow: colors.paper.textShadow,
              wordBreak: 'break-word',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
              mb: 4,
            }}
          >
            {title || 'Untitled'}
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontFamily: 'serif',
              color: isDark ? '#cbd5e1' : colors.text.secondary,
              fontWeight: 'normal',
              fontStyle: 'italic',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem', lg: '1.75rem' },
            }}
          >
            by {pseudonym || 'Anonymous'}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

/* eslint-disable react/require-default-props */
TitlePage.propTypes = {
  title: PropTypes.string,
  wordCount: PropTypes.number,
  pseudonym: PropTypes.string,
};
/* eslint-enable react/require-default-props */

export default TitlePage;
